import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { bootstrap } from '@/api/endpoints'
import type { Bootstrap, Codebook } from '@/types/api'

/**
 * Predvolené názvy číselníkov v databáze školy (`sql/001b_codebooks.sql`) -
 * po slovensky. Kým ich škola nepremenovala, ukazujú sa v jazyku aplikácie
 * (`codebooks.<číselník>.<kľúč>`); vlastný názov ostáva, ako ho škola zadala.
 */
const DEFAULT_NAMES: Record<'courses' | 'levels' | 'statuses', Record<string, string>> = {
  courses: { SKI: 'lyžiarsky kurz', SNB: 'snowboardový kurz' },
  levels: { BEGIN: 'začiatočník', MEDIUM: 'stredne pokročilý', EXPERT: 'veľmi pokročilý' },
  statuses: { PRE: 'predbežne', DEF: 'definitívne', ZRU: 'zrušené' },
}

function translateBook(t: TFunction, book: keyof typeof DEFAULT_NAMES, rows?: Codebook[]) {
  return rows?.map((row) => {
    const key = (row.key ?? '').toUpperCase()
    const original = DEFAULT_NAMES[book][key]
    return original && (row.name ?? '').trim().toLowerCase() === original
      ? { ...row, name: t(`codebooks.${book}.${key}`, { defaultValue: row.name ?? '' }) }
      : row
  })
}

/** Settings + codebooks. Cached for the whole session - they rarely change.
 *  Predvolené názvy kurzov, úrovní a stavov sú v jazyku aplikácie. */
export function useBootstrap() {
  const { t, i18n } = useTranslation()
  const select = useCallback(
    (data: Bootstrap): Bootstrap => ({
      ...data,
      courses: translateBook(t, 'courses', data.courses) ?? data.courses,
      levels: translateBook(t, 'levels', data.levels) ?? data.levels,
      statuses: translateBook(t, 'statuses', data.statuses) ?? data.statuses,
    }),
    // `i18n.language` - pri prepnutí jazyka sa názvy preložia znova.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  )
  return useQuery<Bootstrap, Error, Bootstrap>({
    queryKey: ['bootstrap'],
    queryFn: bootstrap,
    staleTime: 10 * 60 * 1000,
    select,
  })
}

export function useSettings() {
  const { data } = useBootstrap()
  return data?.settings
}

export interface Codebooks {
  courses: Map<number, Codebook>
  languages: Map<number, Codebook>
  levels: Map<number, Codebook>
  statuses: Map<number, Codebook>
  /**
   * Jazyky, ktoré škola **ponúka** — do výberov pri zakladaní.
   *
   * `languages` má naopak všetky, aj vypnuté: podľa nich sa pomenúvajú staré
   * lekcie. Bez toho by minuloročná nemecká lekcia stratila jazyk vo chvíli,
   * keď škola nemčinu prestane ponúkať.
   */
  activeLanguages: Codebook[]
  /** Názov položky číselníka, alebo `null` keď id nič nezodpovedá. */
  label: (
    book: keyof Omit<Codebooks, 'label' | 'entry' | 'activeLanguages'>,
    id: number | null | undefined,
  ) => string | null
  entry: (
    book: keyof Omit<Codebooks, 'label' | 'entry' | 'activeLanguages'>,
    id: number | null | undefined,
  ) => Codebook | null
}

/**
 * Číselníky ako mapy podľa id.
 *
 * Klient nesie `levelid`, `lang_id` a `course_id` ako čísla, takže bez tohto
 * by v zoznamoch svietilo `1` namiesto „začiatočník“.
 */
export function useCodebooks(): Codebooks {
  const { data } = useBootstrap()

  return useMemo(() => {
    const index = (rows?: Codebook[]) => new Map((rows ?? []).map((row) => [row.id, row]))
    const books = {
      courses: index(data?.courses),
      languages: index(data?.languages),
      levels: index(data?.levels),
      statuses: index(data?.statuses),
    }
    const entry: Codebooks['entry'] = (book, id) =>
      id === null || id === undefined ? null : books[book].get(id) ?? null
    return {
      ...books,
      // `use === undefined` sa berie ako zapnuté: staršie odpovede API
      // príznak nemali a prázdna ponuka jazykov by bola horšia než široká.
      activeLanguages: (data?.languages ?? []).filter((row) => row.use !== false),
      entry,
      label: (book, id) => {
        const row = entry(book, id)
        return row ? row.name || row.key : null
      },
    }
  }, [data])
}
